import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { dataUser, spocLocationDetailsZoho } from '../Helpers/util';
import SwSelect from '../Common/SwReactSelect/SwReactSelect';
import SwInput from '../Common/FormComponent/SwInput';
import AntDDatePicker from '../Common/AntDDateTimePicker/AntDDatePicker';
import AntDTimePicker from '../Common/AntDDateTimePicker/AntDTimePicker';

const DeskBookingServiceRequest = forwardRef(({ onClose }, ref) => {
    const UserObj = JSON.parse(dataUser());

    /* ---------- State ---------- */
    const [locationData, setLocationData] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);

    const [category, setCategory] = useState('');
    const [noOfDesk, setNoOfDesk] = useState('');

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    /* ---------- Init ---------- */
    useEffect(() => {
        const locations = JSON.parse(spocLocationDetailsZoho());
        setLocationData(locations);
    }, []);

    /* ---------- Ref methods ---------- */
    useImperativeHandle(ref, () => ({
        resetForm,
        submitRequest,
        loading,
    }));

    /* ---------- Helpers ---------- */
    const setError = (key, msg) =>
        setErrors(prev => ({ ...prev, [key]: msg }));

    const clearError = key =>
        setErrors(prev => ({ ...prev, [key]: '' }));

    /* ---------- Submit ---------- */
    const submitRequest = async () => {
        if (!selectedLocation) return setError('location', "Location can't be empty");
        if (!startDate) return setError('startDate', "Start date can't be empty");
        if (!endDate) return setError('endDate', "End date can't be empty");
        if (!startTime) return setError('startTime', "Start time can't be empty");
        if (!endTime) return setError('endTime', "End time can't be empty");
        if (!category) return setError('category', "Category can't be empty");
        if (!noOfDesk) return setError('noOfDesk', "No. of desk can't be empty");

        setLoading(true);
        const authToken = JSON.parse(localStorage.getItem('headData'));

        const payload = {
            product_name: 'Desk Booking',
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
            start_time: startTime.format('HH:mm'),
            end_time: endTime.format('HH:mm'),
            category,
            noOfDesk,
            company_name: UserObj.company_name,
            email: UserObj.email,
            fname: UserObj.fname,
            lname: UserObj.lname,
            phone: UserObj.phone,
            location_name: selectedLocation.label,
            location_id: selectedLocation.value,
        };

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}basic-data/new-product-enquiry`,
                payload,
                { headers: { Authorization: authToken?.spintlyToken } },
            );


            if (res.status === 200) {

                toast.success(res.data.message);
                onClose(true);
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            }
        } catch (err) {
            toast.error('Unable to submit desk booking request');
            console.error(err);
        } finally {
            setLoading(false);
        }

    }

    /* ---------- Reset ---------- */
    const resetForm = () => {
        setSelectedLocation(null);
        setStartDate(null);
        setEndDate(null);
        setStartTime(null);
        setEndTime(null);
        setCategory('');
        setNoOfDesk('');
        setErrors({});
    };

    /* ---------- UI ---------- */
    return (
        <>
            {/* Location */}
            <div className="SwFormGroup">
                <label className="SwFormLabel">
                    Location <span className="Required">*</span>
                </label>
                <SwSelect
                    value={selectedLocation}
                    options={locationData}
                    onChange={v => {
                        setSelectedLocation(v);
                        clearError('location');
                    }}
                />
                <span className="SwTextDanger SwTextSmall">  {errors.location}</span>
            </div>

            {/* Dates */}
            <div className="row">
                <div className="col-md-6 pr-2">
                    <label className="SwFormLabel">
                        Start Date <span className="Required">*</span>
                    </label>
                    <AntDDatePicker
                        value={startDate}
                        onChange={d => {
                            setStartDate(d);
                            clearError('startDate');
                        }}
                    />
                    <span className="SwTextDanger SwTextSmall"> {errors.startDate}</span>
                </div>

                <div className="col-md-6">
                    <label className="SwFormLabel">
                        End Date <span className="Required">*</span>
                    </label>
                    <AntDDatePicker
                        value={endDate}
                        onChange={d => {
                            setEndDate(d);
                            clearError('endDate');
                        }}
                        minDate={startDate}
                    />
                    <span className="SwTextDanger SwTextSmall">{errors.endDate}</span>
                </div>
            </div>

            {/* Time */}
            <div className="row mt-2">
                <div className="col-md-6 pr-2">
                    <label className="SwFormLabel">
                        Start Time <span className="Required">*</span>
                    </label>
                    <AntDTimePicker
                        value={startTime}
                        format="HH:mm"
                        onChange={t => {
                            setStartTime(t);
                            clearError('startTime');
                        }}
                        placeholder="Start Time"
                    />
                    <span className="SwTextDanger SwTextSmall">{errors.startTime}</span>
                </div>

                <div className="col-md-6">
                    <label className="SwFormLabel">
                        End Time <span className="Required">*</span>
                    </label>
                    <AntDTimePicker
                        value={endTime}
                        format="HH:mm"
                        onChange={t => {
                            setEndTime(t);
                            clearError('endTime');
                        }}
                        placeholder="End Time"
                    />
                    <span className="SwTextDanger SwTextSmall">   {errors.endTime}</span>
                </div>
            </div>

            {/* Category & Desk */}
            <div className="row mt-2">
                <div className="col-md-6 pr-2">
                    <label className="SwFormLabel">
                        Category <span className="Required">*</span>
                    </label>
                    <SwInput
                        value={category}
                        onChange={e => {
                            setCategory(e.target.value);
                            clearError('category');
                        }}
                    />
                    <span className="SwTextDanger SwTextSmall">{errors.category}</span>
                </div>

                <div className="col-md-6">
                    <label className="SwFormLabel">
                        No. of Desk <span className="Required">*</span>
                    </label>
                    <SwInput
                        value={noOfDesk}
                        type="number"
                        min="0"
                        onChange={e => {
                            setNoOfDesk(e.target.value);
                            clearError('noOfDesk');
                        }}
                    />
                    <span className="SwTextDanger SwTextSmall">{errors.noOfDesk}</span>
                </div>
            </div>
        </>
    );
});

export default DeskBookingServiceRequest;
